// Client-side helper for FAA d-TPP (instrument approach plates).
//
// Wraps /api/plates/airport (per-airport plate list) and gives the UI a
// stable PDF-URL builder for /api/plates/pdf. Pure server-backed; no
// IDB caching here since the response is small (a few KB / airport)
// and the server already CDN-caches it for 1h.

export interface PlateRecord {
  ident: string;
  icao: string;
  faa: string;
  airportName: string;
  city: string;
  state: string;
  chartCode: string;     // IAP / DP / DPO / STAR / APD / MIN / HOT / LAH / TO / CVFP
  chartName: string;     // "ILS OR LOC RWY 16"
  pdfName: string;       // "05130IL16.PDF"
  amdtNum: string;       // "0", "5C", ""
  amdtDate: string;      // "5/19/2022", ""
  civil: boolean;
  copter: boolean;
}

export interface PlatesResponse {
  ident: string;
  cycle: string;       // "2604"
  effective: string;   // "2026-04-16"
  expires: string;     // "2026-05-14"
  plates: PlateRecord[];
}

/** Group plates by chart-code into UI-friendly buckets. */
export interface PlateGroups {
  approach: PlateRecord[];   // IAP
  departure: PlateRecord[];  // DP, DPO
  arrival: PlateRecord[];    // STAR
  diagram: PlateRecord[];    // APD
  minimums: PlateRecord[];   // MIN, TO
  other: PlateRecord[];      // HOT, LAH, CVFP, ...
}

/** Fetch the full plate list for an airport. Returns an empty list on 404. */
export async function fetchAirportPlates(ident: string, signal?: AbortSignal): Promise<PlatesResponse> {
  const u = `/api/plates/airport?ident=${encodeURIComponent(ident.toUpperCase())}`;
  const r = await fetch(u, { signal });
  if (!r.ok) {
    throw new Error(`Plates fetch HTTP ${r.status}`);
  }
  return (await r.json()) as PlatesResponse;
}

/** Build the proxy URL for a plate PDF. */
export function platePdfUrl(cycle: string, pdfName: string): string {
  return `/api/plates/pdf?cycle=${encodeURIComponent(cycle)}&pdf=${encodeURIComponent(pdfName)}`;
}

/** Per-plate georef quality flag for the list view. */
export type PlateGeorefQuality =
  | 'ok'
  | 'low-confidence'
  | 'failed'
  | 'corrected'
  | 'pending-review'
  | 'unknown';

export interface PlateGeorefIndexRow {
  pdfName: string;
  status: 'ok' | 'low-confidence' | 'failed';
  residualNm: number | null;
  hasCorrection: boolean;
  /** Review state of the most recent non-superseded correction, when one
   *  exists. Only `approved` corrections are served by /api/plates/georef
   *  for the map overlay, so a pending correction still shows the original
   *  failed/LC status with a pending indicator badge instead of ‘Fixed’. */
  correctionReviewState?: 'pending' | 'approved' | 'rejected' | null;
}

/**
 * Fetch per-plate georef status for an airport. One round trip; covers
 * both the IAP and STAR sibling blobs. Returns a map keyed by pdfName
 * (uppercase). Plates missing from the result have no production georef.
 */
export async function fetchAirportGeorefStatus(
  ident: string,
  cycle: string | null | undefined,
  signal?: AbortSignal,
): Promise<Map<string, PlateGeorefIndexRow>> {
  // Defensive: server returns 400 'cycle required (4 digits)' when the
  // param is missing. Caller should pass a valid cycle, but if the plates
  // response was incomplete or the cycle hasn't resolved yet, just return
  // an empty map so the UI shows 'unknown' badges instead of erroring.
  if (!cycle || !/^\d{4}$/.test(cycle)) return new Map();
  const u =
    `/api/plates/needs-correction?cycle=${encodeURIComponent(cycle)}` +
    `&airport=${encodeURIComponent(ident.toUpperCase())}&include=all`;
  const r = await fetch(u, { signal });
  if (!r.ok) return new Map();
  const body = (await r.json()) as {
    ok: boolean;
    plates?: Array<{
      pdfName: string;
      status: 'ok' | 'low-confidence' | 'failed';
      residualNm: number | null;
      hasCorrection: boolean;
      correctionReviewState?: 'pending' | 'approved' | 'rejected' | null;
    }>;
  };
  if (!body.ok || !body.plates) return new Map();
  const map = new Map<string, PlateGeorefIndexRow>();
  for (const p of body.plates) {
    map.set(p.pdfName.toUpperCase(), p as PlateGeorefIndexRow);
  }
  return map;
}

/** Resolve UI-facing quality from an index row + plate metadata.
 *
 *  Display rules (2026-05-25 fix — boss reported badge wasn’t flipping
 *  after cartographer approval):
 *  - No correction at all          → original status (ok / lc / failed)
 *  - Correction in `pending`       → 'pending-review' (shows original
 *                                     status colour + a small pending dot;
 *                                     map overlay still uses old georef)
 *  - Correction `approved`         → 'corrected'  (✓ Fixed badge; the
 *                                     overlay now uses the manual fit)
 *  - Correction `rejected` (or any
 *    non-active state)             → original status (no override)
 */
export function plateGeorefQuality(
  row: PlateGeorefIndexRow | undefined,
): PlateGeorefQuality {
  if (!row) return 'unknown';
  if (row.hasCorrection) {
    const rs = row.correctionReviewState ?? 'approved';
    if (rs === 'approved') return 'corrected';
    if (rs === 'pending') return 'pending-review';
    // 'rejected' or anything unexpected → fall through to original status
  }
  return row.status;
}

/**
 * Group plates into UI buckets.
 *
 * 2026-05-19 23:55 PT — default flipped from civilOnly=true to
 * civilOnly=false. Boss reported KNLC (NAS Lemoore) showed empty
 * approach list despite the d-TPP feed publishing 12 valid IAP
 * plates there. The civil-only filter was silently dropping all
 * military-only plates, which is wrong for fire-aviation operations:
 * military fields (KNLC, KRIV March ARB, KTIK Tinker, KMTC Selfridge,
 * etc.) ARE routine landing options for wildfires near them, and
 * a pilot needs the published approach data even if they're not
 * permitted to land without coordination. Confirmed widespread:
 * KNLC 12/15 military, KRIV 14/15, KTIK 13/23, KMTC 11/27.
 *
 * Callers can still opt back into civilOnly=true explicitly if they
 * want the hobby-GA behavior.
 */
export function groupPlates(plates: PlateRecord[], { civilOnly = false }: { civilOnly?: boolean } = {}): PlateGroups {
  const groups: PlateGroups = {
    approach: [],
    departure: [],
    arrival: [],
    diagram: [],
    minimums: [],
    other: [],
  };
  for (const p of plates) {
    if (civilOnly && !p.civil) continue;
    switch (p.chartCode) {
      case 'IAP': groups.approach.push(p); break;
      case 'DP':
      case 'DPO': groups.departure.push(p); break;
      case 'STAR': groups.arrival.push(p); break;
      case 'APD': groups.diagram.push(p); break;
      case 'MIN':
      case 'TO': groups.minimums.push(p); break;
      default: groups.other.push(p); break;
    }
  }
  return groups;
}

/**
 * Derive the procedure identifier used as the DB lookup key from the
 * human-readable chart name. Must match the deriveProcIdent() function
 * in api/admin/extract-dtpp-minima.ts — changes here must stay in sync.
 *
 * Examples:
 *   "ILS OR LOC RWY 10R"          → "I10R"
 *   "RNAV (GPS) RWY 28L"          → "R28L"
 *   "RNAV (GPS) Y RWY 09"         → "RY09"
 *   "RNAV (GPS) Z RWY 16C"        → "RZ16C"
 *   "LOC RWY 17"                  → "L17"
 *   "VOR OR GPS RWY 32"           → "V32"
 *
 * Returns null when no runway designator is extractable (circling-only
 * approaches with non-standard names).
 */
export function deriveProcIdent(chartName: string): string | null {
  const name = chartName.toUpperCase().trim();

  // Extract runway designator (e.g. "10R", "28L", "17", "16C", "04-22")
  const rwyMatch = name.match(
    /(?:^|\s)(?:OR\s+)?(?:RWY\s+)?([\dLC R]{1,5}(?:-[\dLC R]{1,5})?)(?:\s|$)/,
  );
  const rwy = rwyMatch ? rwyMatch[1].trim().replace(/\s+/g, '') : null;
  if (!rwy) return null;

  if (/^ILS/.test(name))               return `I${rwy}`;
  if (/^LOC\/DME/.test(name))          return `L${rwy}`;
  if (/^LOC/.test(name))               return `L${rwy}`;
  if (/^RNAV\s*\(GPS\)\s+Y/.test(name)) return `RY${rwy}`;
  if (/^RNAV\s*\(GPS\)\s+Z/.test(name)) return `RZ${rwy}`;
  if (/^RNAV\s*\(GPS\)/.test(name))    return `R${rwy}`;
  if (/^RNAV\s*\(RNP\)\s+Z/.test(name)) return `H${rwy}Z`;
  if (/^RNAV\s*\(RNP\)/.test(name))    return `H${rwy}`;
  if (/^VOR\/DME/.test(name))          return `V${rwy}`;
  if (/^VOR/.test(name))               return `V${rwy}`;
  if (/^NDB\/DME/.test(name))          return `N${rwy}`;
  if (/^NDB/.test(name))               return `N${rwy}`;
  if (/^TACAN/.test(name))             return `T${rwy}`;
  if (/^LDA/.test(name))               return `D${rwy}`;
  if (/^SDF/.test(name))               return `S${rwy}`;

  const prefix = name.replace(/[^A-Z]/g, '').substring(0, 2);
  return `${prefix}${rwy}`;
}

/** Fetch circling minima for a single IAP plate from Supabase-backed API. */
export interface CirclingMinimaApiResponse {
  minima: {
    airport_icao: string;
    proc_ident: string;
    cycle: string;
    circling_mda_a: number | null;
    circling_mda_b: number | null;
    circling_mda_c: number | null;
    circling_mda_d: number | null;
    circling_mda_e: number | null;
    circling_vis_a: string | null;
    circling_vis_b: string | null;
    circling_vis_c: string | null;
    circling_vis_d: string | null;
    circling_vis_e: string | null;
    has_expanded_circling: boolean | null;
  } | null;
  variations: Array<{
    raw_text: string;
    restriction_kind: string;
    condition_text: string | null;
    sectors: unknown | null;
  }>;
  cycle: string | null;
}

export async function fetchCirclingMinima(
  airportIcao: string,
  procIdent: string,
  signal?: AbortSignal,
): Promise<CirclingMinimaApiResponse | null> {
  const u = `/api/plates/circling-minima?airport=${encodeURIComponent(airportIcao)}&proc=${encodeURIComponent(procIdent)}`;
  const r = await fetch(u, { signal });
  if (!r.ok) return null;
  return (await r.json()) as CirclingMinimaApiResponse;
}

export function chartCodeLabel(code: string): string {
  switch (code) {
    case 'IAP': return 'Approach';
    case 'DP': return 'Departure';
    case 'DPO': return 'Obstacle DP';
    case 'STAR': return 'Arrival';
    case 'APD': return 'Airport Diagram';
    case 'MIN': return 'Alt Mins';
    case 'TO': return 'Takeoff Mins';
    case 'HOT': return 'Hot Spots';
    case 'LAH': return 'LAHSO';
    case 'CVFP': return 'Charted VFR';
    default: return code || '—';
  }
}
